// Small one-shot building blocks for synthesized sounds (sounds.ts, the sound lab).
// Each schedules its own nodes at an absolute AudioContext time and lets them
// end on their own, like audio/synth.ts.
import { getNoiseBuffer, mtof, note } from "./synth";

export { mtof, note };
export type Play = (ctx: AudioContext, dest: AudioNode, t: number) => void;

type Wave = OscillatorType;

// A tone with an exponential pitch glide and a fast-attack, exponential-decay envelope.
export function tone(ctx: AudioContext, dest: AudioNode, o: { t: number; f: number; to?: number; dur: number; type?: Wave; gain?: number; attack?: number; cutoff?: number; detune?: number }) {
  const osc = ctx.createOscillator();
  osc.type = o.type || "sine";
  osc.frequency.setValueAtTime(o.f, o.t);
  if (o.to) osc.frequency.exponentialRampToValueAtTime(o.to, o.t + o.dur);
  if (o.detune) osc.detune.value = o.detune;
  const g = ctx.createGain();
  const peak = o.gain ?? 0.2;
  const a = o.attack ?? 0.004;
  g.gain.setValueAtTime(0.0001, o.t);
  g.gain.exponentialRampToValueAtTime(peak, o.t + a);
  g.gain.exponentialRampToValueAtTime(0.0001, o.t + o.dur);
  let node: AudioNode = osc;
  if (o.cutoff) {
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = o.cutoff;
    osc.connect(lp);
    node = lp;
  }
  node.connect(g);
  g.connect(dest);
  osc.start(o.t);
  osc.stop(o.t + o.dur + 0.05);
}

// Filtered noise: a burst (whoosh, crunch, hiss) with a sweeping filter.
export function noise(ctx: AudioContext, dest: AudioNode, o: { t: number; dur: number; gain?: number; type?: BiquadFilterType; f: number; to?: number; q?: number; attack?: number }) {
  const src = ctx.createBufferSource();
  src.buffer = getNoiseBuffer(ctx);
  src.loop = true;
  const f = ctx.createBiquadFilter();
  f.type = o.type || "bandpass";
  f.Q.value = o.q ?? 1;
  f.frequency.setValueAtTime(o.f, o.t);
  if (o.to) f.frequency.exponentialRampToValueAtTime(o.to, o.t + o.dur);
  const g = ctx.createGain();
  const a = o.attack ?? 0.005;
  g.gain.setValueAtTime(0.0001, o.t);
  g.gain.exponentialRampToValueAtTime(o.gain ?? 0.3, o.t + a);
  g.gain.exponentialRampToValueAtTime(0.0001, o.t + o.dur);
  src.connect(f);
  f.connect(g);
  g.connect(dest);
  src.start(o.t);
  src.stop(o.t + o.dur + 0.05);
}

// Notes one after another (MIDI numbers), each a short tone.
export function arp(ctx: AudioContext, dest: AudioNode, t: number, notes: number[], o: { step: number; dur: number; type?: Wave; gain?: number; cutoff?: number }) {
  notes.forEach((m, i) => tone(ctx, dest, { t: t + i * o.step, f: mtof(m), dur: o.dur, type: o.type, gain: o.gain, cutoff: o.cutoff }));
}

// A chord: all notes at once, slightly detuned for width.
export function chord(ctx: AudioContext, dest: AudioNode, t: number, notes: number[], o: { dur: number; type?: Wave; gain?: number; cutoff?: number; attack?: number }) {
  notes.forEach((m, i) => tone(ctx, dest, { t, f: mtof(m), dur: o.dur, type: o.type || "sawtooth", gain: o.gain ?? 0.07, cutoff: o.cutoff ?? 3000, attack: o.attack ?? 0.01, detune: (i % 2 ? 1 : -1) * 6 }));
}

// N(name, octave) shorthand.
export const N = note;
